import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import { useNotify, useUpdate, type RaRecord } from "react-admin";

type RelayEventRecord = RaRecord & {
  isLive?: boolean;
};

type RelayEventLiveToggleProps = {
  record: RelayEventRecord;
  canEdit: boolean;
};

export function RelayEventLiveToggle({ record, canEdit }: RelayEventLiveToggleProps) {
  const [update, { isPending }] = useUpdate();
  const notify = useNotify();

  if (!record?.id) {
    return null;
  }

  if (!canEdit) {
    return record.isLive ? (
      <Chip label="Live" size="small" color="success" />
    ) : (
      <Chip label="Off" size="small" variant="outlined" />
    );
  }

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const nextLive = event.target.checked;

    update(
      "relay-events",
      {
        id: record.id,
        data: { isLive: nextLive },
        previousData: record,
      },
      {
        onSuccess: () => {
          notify(nextLive ? "Event is now live" : "Event is no longer live", { type: "info" });
        },
        onError: (error) => {
          notify(error?.message ?? "Could not update live status", { type: "error" });
        },
      }
    );
  };

  return (
    <Box
      component="span"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Switch
        size="small"
        checked={Boolean(record.isLive)}
        disabled={isPending}
        onChange={handleToggle}
        color="success"
        inputProps={{
          "aria-label": record.isLive ? "Stop live stream" : "Go live",
        }}
      />
    </Box>
  );
}
